import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { S } from '../styles';
import { getTheme } from '../theme';
import { API, authHeaders } from '../utils/helpers';
import { useTextingReady, TEXTING_OFF_TITLE } from '../utils/texting';

// Centralized Payments ledger — every dollar Spinboard has moved for this
// shop, text payments and bookings unified, independent of ticket/booking
// state. Table layout is deliberate: this is a financial record, and it
// should read like one.

const STATUS_META = {
  paid:           { label: 'Paid',     color: '#2ecc71' },
  captured:       { label: 'Paid',     color: '#2ecc71' },
  authorized:     { label: 'Hold',     color: '#e67e22', hint: 'Card authorized, not yet charged. Cancel the booking in the Bookings module to release this hold — no charge is ever made and there is no fee.' },
  pending:        { label: 'Sent',     color: '#e67e22' },
  failed:         { label: 'Declined', color: '#e74c3c' },
  capture_failed: { label: 'Declined', color: '#e74c3c' },
  expired:        { label: 'Expired',  color: '#7a8ba8' },
  refunded:       { label: 'Refunded', color: '#7a8ba8' },
  partially_refunded: { label: 'Partial refund', color: '#e67e22' },
  voided:         { label: 'Voided',   color: '#7a8ba8' },
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const moneyShort = (n) => {
  const v = Number(n || 0);
  return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`;
};
const cardLabel = (brand, last4) => brand ? `${brand.charAt(0).toUpperCase()}${brand.slice(1)} •••• ${last4}` : '—';
const fmtPhone = (p) => {
  const d = (p || '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (p || '');
};
const fmtDate = (ts) => ts ? new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

function Payments({ theme = 'dark', currentUser = null, newSince = null }) {
  // App.js has always passed currentUser; the signature dropped it.
  // Refund and Retry LS move real money. The tab itself is visible to
  // associates, who must not. Fails closed on a missing permission map.
  const canRefund = !!currentUser?.permissions?.payments_refund;
  const T = getTheme(theme);
  const isDark = theme === 'dark';
  const [payments, setPayments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [hoverRow, setHoverRow] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);

  const [refundTarget, setRefundTarget] = useState(null);
  const [feePreview, setFeePreview] = useState(null);
  const [working, setWorking] = useState(false);
  const [rowError, setRowError] = useState({});

  // New request modal
  const [showRequest, setShowRequest] = useState(false);
  const [custQuery, setCustQuery] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [custSearching, setCustSearching] = useState(false);
  const [selectedCust, setSelectedCust] = useState(null);
  const [reqAmount, setReqAmount] = useState('');
  const [reqDesc, setReqDesc] = useState('');
  const textingReady = useTextingReady();
  const [reqChannel, setReqChannel] = useState('sms');
  const [reqError, setReqError] = useState('');
  const [supersedesId, setSupersedesId] = useState(null);
  const [reqSending, setReqSending] = useState(false);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, aRes, sRes] = await Promise.all([
        fetch(`${API}/api/payments/all`, { credentials: 'include', headers: authHeaders() }),
        fetch(`${API}/api/payments/analytics`, { credentials: 'include', headers: authHeaders() }),
        fetch(`${API}/api/stripe-connect/status`, { credentials: 'include', headers: authHeaders() }),
      ]);
      const pData = await pRes.json();
      const aData = await aRes.json();
      const sData = await sRes.json();
      setPayments(Array.isArray(pData.payments) ? pData.payments : []);
      setAnalytics(aRes.ok ? aData : null);
      setStripeStatus(sRes.ok ? sData : null);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector('main')?.scrollTo?.(0, 0);
  }, []);

  const rowKey = (p) => `${p.sourceType}:${p.sourceId}`;

  // Which rows the sidebar badge was counting when this user walked in.
  //
  // `newSince` is App.js's snapshot of this user's payments_viewed_at, frozen
  // one instant before opening the module moved it forward. It is per-user, so
  // two people at the counter can legitimately see different rows flagged.
  //
  // Keyed on p.paidAt, never p.timestamp. `timestamp` falls back to the row's
  // created/updated time so the Date column is never blank, which would make
  // "link sent" and "booking authorized" look like "customer paid". `paidAt`
  // is null until money actually moves — paid_at on a payment link,
  // captured_at on a booking — and is the same clock countNewPayments counts
  // on, so a badge and the sidebar number can never disagree.
  //
  // An authorized booking hold therefore never badges: at this shop most
  // bookings sit authorized until the day-X sweep collects, and a hold is not
  // a payment. 'refunded'/'voided' are out too — the money came and went.
  const newSinceMs = newSince ? new Date(newSince).getTime() : null;
  const PAID_STATES = ['paid', 'captured', 'partially_refunded'];
  const isNewPayment = (p) => (
    newSinceMs !== null
    && PAID_STATES.includes(p.status)
    && p.paidAt
    && new Date(p.paidAt).getTime() > newSinceMs
  );

  const openRefundConfirm = async (p) => {
    setRefundTarget(p);
    setFeePreview('loading');
    try {
      const res = await fetch(`${API}/api/payments/refund-preview?sourceType=${p.sourceType}&id=${p.sourceId}`, {
        credentials: 'include', headers: authHeaders(),
      });
      const data = await res.json();
      setFeePreview(res.ok ? data : null);
    } catch (_) { setFeePreview(null); }
  };

  const closeRefundConfirm = () => { setRefundTarget(null); setFeePreview(null); };

  const runAction = async (p, path, label) => {
    setWorking(true);
    try {
      const res = await fetch(path, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json', ...authHeaders() } });
      const data = await res.json();
      if (!res.ok) setRowError(prev => ({ ...prev, [rowKey(p)]: data.error || `${label} failed.` }));
      else if (data.warning) setRowError(prev => ({ ...prev, [rowKey(p)]: data.warning }));
      else setRowError(prev => { const n = { ...prev }; delete n[rowKey(p)]; return n; });
      load();
    } catch (_) {
      setRowError(prev => ({ ...prev, [rowKey(p)]: `${label} failed — check manually.` }));
    }
    setWorking(false);
  };

  const doRefund = async () => {
    if (!refundTarget) return;
    const p = refundTarget;
    closeRefundConfirm();
    await runAction(p, p.sourceType === 'text'
      ? `${API}/api/payments/${p.sourceId}/refund`
      : `${API}/book/staff/${p.sourceId}/refund`, 'Refund');
  };

  const doRetry = (p) => runAction(p, p.sourceType === 'text'
    ? `${API}/api/payments/${p.sourceId}/retry-ls-reversal`
    : `${API}/book/staff/${p.sourceId}/retry-ls-reversal`, 'Retry');

  // Debounced Lightspeed customer search — same endpoint NewTicketModal uses.
  useEffect(() => {
    if (selectedCust || custQuery.trim().length < 2) { setCustResults([]); return; }
    const t = setTimeout(async () => {
      setCustSearching(true);
      try {
        const res = await fetch(`${API}/api/customers?q=${encodeURIComponent(custQuery.trim())}`, { credentials: 'include', headers: authHeaders() });
        const data = await res.json();
        setCustResults(Array.isArray(data) ? data.slice(0, 8) : []);
      } catch (_) { setCustResults([]); }
      setCustSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [custQuery, selectedCust]);

  const resetRequest = () => {
    setShowRequest(false); setCustQuery(''); setCustResults([]); setSelectedCust(null);
    setReqAmount(''); setReqDesc(''); setReqChannel(textingReady ? 'sms' : 'email'); setReqError(''); setSupersedesId(null);
  };

  const pickCustomer = (c) => {
    setSelectedCust(c);
    setCustQuery('');
    setCustResults([]);
    // Default to whichever channel the customer can actually receive.
    setReqChannel(c.phone && textingReady ? 'sms' : (c.email ? 'email' : 'sms'));
  };

  // Opens the request modal prefilled from an expired row. Deliberately not a
  // one-click resend: a ticket may have grown since the original link was
  // created, and a blind clone would quietly undercharge.
  const openResend = (p) => {
    setSelectedCust({
      id: p.lsCustomerId || null,
      name: p.customerName || '',
      phone: p.customerPhone || '',
      email: p.customerEmail || '',
    });
    setReqAmount(Number(p.amount || 0).toFixed(2));
    setReqDesc(p.description || '');
    setReqChannel(p.channel === 'email' ? 'email' : 'sms');
    setSupersedesId(p.sourceId);
    setReqError('');
    setShowRequest(true);
  };

  const submitRequest = async () => {
    setReqError('');
    if (!selectedCust) return setReqError('Pick a customer first.');
    const amt = Number(reqAmount);
    if (!amt || amt <= 0) return setReqError('Enter an amount greater than zero.');
    if (amt > 20000) return setReqError('Payment links are capped at $20,000.00.');
    if (reqChannel === 'sms' && !textingReady) return setReqError(`${TEXTING_OFF_TITLE}. Send it by email.`);
    if (reqChannel === 'sms' && !selectedCust.phone) return setReqError('This customer has no phone number on file.');
    if (reqChannel === 'email' && !selectedCust.email) return setReqError('This customer has no email address on file.');

    setReqSending(true);
    try {
      const res = await fetch(`${API}/api/payments/send`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          lsCustomerId: selectedCust.id,
          customerName: selectedCust.name,
          customerPhone: selectedCust.phone || null,
          customerEmail: selectedCust.email || null,
          amount: amt,
          description: reqDesc.trim() || null,
          channel: reqChannel,
          supersedes: supersedesId,
        }),
      });
      const data = await res.json();
      if (!res.ok) setReqError(data.error || 'Could not send the payment link.');
      else {
        setToast(`Payment link sent to ${selectedCust.name} by ${reqChannel === 'sms' ? 'text' : 'email'}`);
        setTimeout(() => setToast(''), 4000);
        resetRequest();
        load();
      }
    } catch (_) {
      setReqError('Could not send the payment link.');
    }
    setReqSending(false);
  };

  const counts = useMemo(() => ({
    all: payments.length,
    paid: payments.filter(p => p.status === 'paid' || p.status === 'captured').length,
    outstanding: payments.filter(p => p.status === 'pending').length,
    expired: payments.filter(p => p.status === 'expired').length,
    refunded: payments.filter(p => ['refunded', 'voided', 'partially_refunded'].includes(p.status)).length,
    failed: payments.filter(p => p.status === 'failed' || p.status === 'capture_failed').length,
  }), [payments]);

  const filtered = useMemo(() => {
    let rows = payments;
    if (statusFilter === 'paid') rows = rows.filter(p => p.status === 'paid' || p.status === 'captured');
    else if (statusFilter === 'outstanding') rows = rows.filter(p => p.status === 'pending');
    else if (statusFilter === 'expired') rows = rows.filter(p => p.status === 'expired');
    else if (statusFilter === 'refunded') rows = rows.filter(p => ['refunded', 'voided', 'partially_refunded'].includes(p.status));
    else if (statusFilter === 'failed') rows = rows.filter(p => p.status === 'failed' || p.status === 'capture_failed');
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(p => (p.customerName || '').toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q));
    }
    return rows;
  }, [payments, statusFilter, search]);

  const inputStyle = { background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '8px 12px', color: T.text, fontSize: 13, fontFamily: 'Arial, sans-serif', outline: 'none', boxSizing: 'border-box' };
  const thStyle = { textAlign: 'left', padding: '11px 14px', color: T.textSub, fontWeight: 700, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '11px 14px', fontSize: 13, color: T.text, verticalAlign: 'middle' };
  const headBg = isDark ? '#1a2336' : '#f4f3ee';
  const hoverBg = isDark ? '#1c2740' : '#f7f6f1';

  const PILLS = [
    { id: 'all', label: 'All', n: counts.all },
    { id: 'paid', label: 'Paid', n: counts.paid, color: '#2ecc71' },
    { id: 'outstanding', label: 'Outstanding', n: counts.outstanding, color: '#e67e22' },
    { id: 'expired', label: 'Expired', n: counts.expired, color: '#7a8ba8' },
    { id: 'refunded', label: 'Refunded', n: counts.refunded, color: '#7a8ba8' },
    { id: 'failed', label: 'Declined', n: counts.failed, color: '#e74c3c' },
  ];

  const outcomes = analytics?.refundOutcomes;
  const outcomeRows = outcomes ? [
    { label: 'Reversed cleanly', n: outcomes.reversed, color: '#2ecc71', sub: 'Lightspeed credit returned' },
    { label: 'Needs manual Return', n: outcomes.refusedSpent, color: '#e67e22', sub: 'credit already spent at register' },
    { label: 'Reversal failed', n: outcomes.failed, color: '#e74c3c', sub: 'retry from the table below' },
    { label: 'No deposit on record', n: outcomes.noDeposit, color: T.textSub, sub: 'nothing to reverse' },
  ] : [];
  const outcomeMax = Math.max(1, ...outcomeRows.map(o => o.n));

  const src = analytics?.bySource;
  const srcMax = Math.max(1, src?.text?.total || 0, src?.booking?.total || 0);

  return (
    <div>
      <div style={{ ...S.topbar, background: T.topbar, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div style={{ ...S.pageTitle, color: T.text }}>Payments</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {stripeStatus && (() => {
            // chargesEnabled === null means Stripe didn't report the field —
            // treat that as "connected, nothing known to be wrong" rather
            // than asserting a problem.
            const chargesBad = stripeStatus.chargesEnabled === false;
            const ok = stripeStatus.connected && stripeStatus.verified && !chargesBad;
            const dot = ok ? '#2ecc71' : (stripeStatus.connected ? '#e74c3c' : T.textSub);
            const label = !stripeStatus.connected ? 'Stripe not connected'
              : !stripeStatus.verified ? 'Stripe account unreachable'
              : chargesBad ? `Stripe charges disabled${stripeStatus.disabledReason ? ` (${stripeStatus.disabledReason})` : ''}`
              : 'Stripe connected';
            const tip = stripeStatus.accountId
              ? `${stripeStatus.businessName ? `${stripeStatus.businessName} · ` : ''}${stripeStatus.accountId}${stripeStatus.verifyError ? ` — ${stripeStatus.verifyError}` : ''}`
              : 'Connect a Stripe account in Settings';
            return (
              <span title={tip} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: ok ? T.textSub : dot, fontWeight: ok ? 400 : 600, cursor: 'default' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, flexShrink: 0 }} />
                {label}
              </span>
            );
          })()}
          <span style={{ fontSize: 11, color: T.textSub }}>All time · text payments and bookings</span>
          <button style={{ ...S.btn(true), background: '#e67e22', color: '#fff', fontSize: 13 }}
            onClick={() => setShowRequest(true)}>+ New payment request</button>
        </div>
      </div>

      <div style={{ padding: '16px 20px 32px' }}>
        {/* KPI row */}
        {analytics && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 12 }}>
            {[
              { label: 'Total collected', value: money(analytics.collected), sub: `${analytics.paidCount} payment${analytics.paidCount === 1 ? '' : 's'}`, accent: '#3498db' },
              { label: 'Net after refunds', value: money(analytics.net), sub: `${analytics.retainedPct}% retained`, accent: '#2ecc71' },
              { label: 'Outstanding', value: money(analytics.outstandingLive), sub: `${analytics.outstandingLiveCount} awaiting · ${money(analytics.outstandingExpired)} expired`, accent: '#e67e22' },
              { label: 'Refunded', value: money(analytics.refunded), sub: `${analytics.refundCount} transaction${analytics.refundCount === 1 ? '' : 's'}`, accent: '#e74c3c' },
            ].map(({ label, value, sub, accent }) => (
              <div key={label} style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderTop: `3px solid ${accent}`, borderRadius: 10, padding: '16px 18px' }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: T.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{label}</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: accent, marginBottom: 4 }}>{value}</div>
                <div style={{ fontSize: 11, color: T.textSub }}>{sub}</div>
              </div>
            ))}
          </div>
        )}

        {/* Panels */}
        {analytics && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: '16px 20px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Revenue by source</div>
              {[
                { label: 'Payment links', d: src?.text, color: '#3498db' },
                { label: 'Bookings', d: src?.booking, color: '#e67e22' },
              ].map(({ label, d, color }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{label}</div>
                    <div style={{ fontSize: 11, color: T.textSub }}>{d?.count || 0} transaction{(d?.count || 0) === 1 ? '' : 's'}</div>
                  </div>
                  <div style={{ width: 70, height: 6, borderRadius: 3, background: isDark ? '#243050' : '#e0e0d8', overflow: 'hidden' }}>
                    <div style={{ width: `${Math.round(((d?.total || 0) / srcMax) * 100)}%`, height: '100%', background: color }} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color, minWidth: 56, textAlign: 'right' }}>{moneyShort(d?.total)}</div>
                </div>
              ))}
            </div>

            <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: '16px 20px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Refund outcomes</div>
              {analytics.refundCount === 0 ? (
                <div style={{ fontSize: 12, color: T.textSub }}>No refunds issued yet.</div>
              ) : outcomeRows.map(o => (
                <div key={o.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{o.label}</div>
                    <div style={{ fontSize: 11, color: T.textSub }}>{o.sub}</div>
                  </div>
                  <div style={{ width: 70, height: 6, borderRadius: 3, background: isDark ? '#243050' : '#e0e0d8', overflow: 'hidden' }}>
                    <div style={{ width: `${Math.round((o.n / outcomeMax) * 100)}%`, height: '100%', background: o.color }} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: o.color, minWidth: 20, textAlign: 'right' }}>{o.n}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter pills + search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          {PILLS.map(p => {
            const active = statusFilter === p.id;
            const c = p.color || T.textSub;
            return (
              <button key={p.id} onClick={() => setStatusFilter(p.id)}
                style={{
                  border: `1.5px solid ${active ? c : T.border}`,
                  background: active ? `${c}1f` : 'transparent',
                  color: active ? c : T.textSub,
                  borderRadius: 20, padding: '5px 14px', fontSize: 12,
                  fontWeight: active ? 700 : 500, cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif", transition: 'all 0.12s',
                }}>
                {p.label} ({p.n})
              </button>
            );
          })}
          <input style={{ ...inputStyle, marginLeft: 'auto', width: 260 }} placeholder="Search customer or description"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {loading && <div style={{ color: T.textSub, padding: 40 }}>Loading payments…</div>}
        {!loading && filtered.length === 0 && (
          <div style={{ color: T.textSub, padding: 40, textAlign: 'center', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 10 }}>
            No payments match this filter.
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: headBg, borderBottom: `1px solid ${T.cardBorder}` }}>
                  <th style={thStyle}>Customer</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Description</th>
                  <th style={thStyle}>Sent via</th>
                  <th style={thStyle}>Card</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Refunded</th>
                  <th style={{ ...thStyle, textAlign: 'right', minWidth: 150 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const meta = p.supersededBy
                          ? { label: 'Replaced', color: '#7a8ba8' }
                          : (STATUS_META[p.status] || { label: p.status, color: T.textSub });
                  const key = rowKey(p);
                  const err = rowError[key];
                  const needsRetry = p.lsReversalStatus === 'failed';
                  const hasDetail = p.declineReason || p.depositError || err;
                  const isNew = isNewPayment(p);
                  const bg = hoverRow === key ? hoverBg : 'transparent';
                  return (
                    <React.Fragment key={key}>
                      <tr onMouseEnter={() => setHoverRow(key)} onMouseLeave={() => setHoverRow(null)}
                        style={{ background: bg, borderTop: `1px solid ${T.cardBorder}`, transition: 'background 0.1s' }}>
                        <td style={{ ...tdStyle, fontWeight: 500 }}>{p.customerName || 'Unknown'}</td>
                        <td style={{ ...tdStyle, color: T.textSub }}>{p.sourceType === 'text' ? 'Payment link' : 'Booking'}</td>
                        <td style={{ ...tdStyle, color: T.textSub }}>
                          {p.description || ''}{p.workorderId ? ` · #${p.workorderId}` : ''}
                          {/* Green, matching the sidebar badge that sent the
                              user here, so the two read as the same signal.
                              Same pill geometry as the status cell rather than
                              a new shape. */}
                          {isNew && (
                            <span title="Paid since you last opened Payments"
                              style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                                textTransform: 'uppercase', padding: '2px 7px', borderRadius: 10,
                                background: '#27ae6022', color: '#27ae60', whiteSpace: 'nowrap' }}>
                              New
                            </span>
                          )}
                        </td>
                        <td style={{ ...tdStyle, color: T.textSub, fontSize: 12 }}>
                          {p.sourceType === 'booking' ? '—' : (p.channel === 'email' ? 'Email' : 'Text')}
                        </td>
                        <td style={{ ...tdStyle, color: T.textSub, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11.5, whiteSpace: 'nowrap' }}>
                          {cardLabel(p.cardBrand, p.cardLast4)}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700 }}>{money(p.amount)}</td>
                        <td style={tdStyle}>
                          <span title={meta.hint || undefined}
                            style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12, background: `${meta.color}22`, color: meta.color, whiteSpace: 'nowrap', cursor: meta.hint ? 'help' : 'default' }}>{meta.label}</span>
                        </td>
                        <td style={{ ...tdStyle, color: T.textSub, whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(p.timestamp)}</td>
                        <td style={{ ...tdStyle, color: T.textSub, whiteSpace: 'nowrap', fontSize: 12 }}>{p.refundedAt ? fmtDate(p.refundedAt) : '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {needsRetry && canRefund && (
                            <button style={{ ...S.btn(false), fontSize: 11, padding: '4px 10px', color: '#e67e22', borderColor: '#e67e2233', marginRight: 6 }}
                              disabled={working} onClick={() => doRetry(p)}>Retry LS</button>
                          )}
                          {p.status === 'expired' && !p.supersededBy && (
                            <button style={{ ...S.btn(false), fontSize: 11, padding: '4px 10px', color: '#3498db', borderColor: '#3498db33' }}
                              disabled={working} onClick={() => openResend(p)}>Resend</button>
                          )}
                          {p.refundable && canRefund && (
                            <button style={{ ...S.btn(false), fontSize: 11, padding: '4px 10px', color: '#e74c3c', borderColor: '#e74c3c33' }}
                              disabled={working} onClick={() => openRefundConfirm(p)}>Refund</button>
                          )}
                          {!(needsRetry && canRefund) && !(p.refundable && canRefund) && !(p.status === 'expired' && !p.supersededBy) && (
                            <span style={{ color: T.textMuted, fontSize: 12 }}>—</span>
                          )}
                        </td>
                      </tr>
                      {hasDetail && (
                        <tr onMouseEnter={() => setHoverRow(key)} onMouseLeave={() => setHoverRow(null)} style={{ background: bg }}>
                          <td colSpan={10} style={{ padding: '0 14px 10px' }}>
                            {p.declineReason && <div style={{ fontSize: 11.5, color: '#e74c3c' }}>Decline reason: {p.declineReason}</div>}
                            {p.status === 'failed' && p.url && (
                              <div style={{ fontSize: 11.5, color: T.textSub, marginTop: 3 }}>
                                Link is live — deliver it manually:{' '}
                                <span style={{ fontFamily: "'IBM Plex Mono', monospace", wordBreak: 'break-all' }}>{p.url}</span>
                                <button onClick={() => navigator.clipboard.writeText(p.url)}
                                  style={{ ...S.btn(false), fontSize: 10.5, padding: '2px 8px', marginLeft: 8, color: T.textSub, borderColor: T.border }}>Copy</button>
                              </div>
                            )}
                            {p.depositError && <div style={{ fontSize: 11.5, color: '#e74c3c', fontWeight: 600, marginTop: 2 }}>⚠ {p.depositError}</div>}
                            {err && <div style={{ fontSize: 11.5, color: '#e67e22', fontWeight: 600, marginTop: 2 }}>⚠ {err}</div>}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {refundTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={closeRefundConfirm}>
          <div style={{ background: T.card, borderRadius: 12, padding: '20px 24px', width: 400, border: `1px solid ${T.cardBorder}` }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 8 }}>Refund this payment?</div>
            <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.6, marginBottom: 16 }}>
              {money(refundTarget.amount)} will be refunded to {refundTarget.customerName || 'the customer'}'s card.
              {feePreview === 'loading' && ' Looking up the processing fee…'}
              {feePreview && feePreview !== 'loading' && feePreview.feeAmount != null && ` Stripe's processing fee (${money(feePreview.feeAmount)}) is not returned to the shop.`}
              {' '}The Lightspeed deposit will be reversed if it's still unspent — if it's already been applied to a sale, you'll be told which one to process a manual Return against.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={{ ...S.btn(false), fontSize: 13, padding: '7px 16px', color: T.text, borderColor: T.border }} disabled={working} onClick={closeRefundConfirm}>Cancel</button>
              <button style={{ ...S.btn(true), fontSize: 13, padding: '7px 16px', background: '#e74c3c', color: '#fff' }} disabled={working} onClick={doRefund}>
                {working ? 'Refunding…' : `Refund ${money(refundTarget.amount)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRequest && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={resetRequest}>
          <div style={{ background: T.card, borderRadius: 12, padding: '22px 26px', width: 460, border: `1px solid ${T.cardBorder}` }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>New payment request</div>
              <button onClick={resetRequest} style={{ background: 'transparent', border: 'none', color: T.textSub, fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>

            {/* Customer */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 5 }}>Customer</div>
              {selectedCust ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: isDark ? '#1a2336' : '#f4f3ee', border: `1px solid ${T.cardBorder}`, borderRadius: 7, padding: '9px 12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: T.text, marginBottom: 3 }}>{selectedCust.name}</div>
                    <div style={{ fontSize: 11.5, color: T.textSub, lineHeight: 1.7 }}>
                      {selectedCust.phone ? fmtPhone(selectedCust.phone) : 'no phone'}
                      <span style={{ margin: '0 6px', opacity: 0.5 }}>·</span>
                      {selectedCust.email || 'no email'}
                    </div>
                  </div>
                  <button onClick={() => { setSelectedCust(null); setCustQuery(''); }}
                    style={{ ...S.btn(false), fontSize: 11, padding: '4px 10px', color: T.textSub, borderColor: T.border }}>Change</button>
                </div>
              ) : (
                <>
                  <input style={{ ...inputStyle, width: '100%' }} autoFocus placeholder="Search by name or phone"
                    value={custQuery} onChange={e => setCustQuery(e.target.value)} />
                  {custSearching && <div style={{ fontSize: 11.5, color: T.textSub, marginTop: 5 }}>Searching…</div>}
                  {custResults.length > 0 && (
                    <div style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 7, marginTop: 6, maxHeight: 190, overflowY: 'auto' }}>
                      {custResults.map(c => (
                        <div key={c.id} onClick={() => pickCustomer(c)}
                          style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${T.cardBorder}` }}
                          onMouseEnter={e => e.currentTarget.style.background = hoverBg}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <div style={{ fontSize: 13, color: T.text, marginBottom: 3 }}>{c.name}</div>
                          <div style={{ fontSize: 11.5, color: T.textSub, lineHeight: 1.7 }}>
                            {c.phone ? fmtPhone(c.phone) : 'no phone'}
                            {c.email ? <><span style={{ margin: '0 6px', opacity: 0.5 }}>·</span>{c.email}</> : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Amount + description */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <div style={{ flex: '0 0 130px' }}>
                <div style={{ fontSize: 12, color: T.textSub, marginBottom: 5 }}>Amount</div>
                <input type="number" step="0.01" min="0" placeholder="0.00" style={{ ...inputStyle, width: '100%' }}
                  value={reqAmount} onChange={e => setReqAmount(e.target.value)} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: T.textSub, marginBottom: 5 }}>Description</div>
                <input type="text" placeholder="What is this for?" style={{ ...inputStyle, width: '100%' }}
                  value={reqDesc} onChange={e => setReqDesc(e.target.value)} />
              </div>
            </div>

            {/* Channel */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 6 }}>Send by</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { id: 'sms', label: 'Text message', ok: textingReady && (!selectedCust || !!selectedCust.phone),
                    why: textingReady ? 'This customer has no phone on file' : 'Texting is not switched on for your shop yet' },
                  { id: 'email', label: 'Email', ok: !selectedCust || !!selectedCust.email, missing: 'no email on file' },
                ].map(ch => {
                  const active = reqChannel === ch.id;
                  return (
                    <button key={ch.id} disabled={!ch.ok} onClick={() => setReqChannel(ch.id)}
                      title={ch.ok ? '' : (ch.why || `This customer has ${ch.missing}`)}
                      style={{
                        flex: 1, borderRadius: 7, padding: '9px 12px', fontSize: 13,
                        fontFamily: "'Inter', sans-serif", fontWeight: active ? 600 : 500,
                        cursor: ch.ok ? 'pointer' : 'not-allowed', opacity: ch.ok ? 1 : 0.4,
                        border: `1.5px solid ${active ? '#3498db' : T.border}`,
                        background: active ? '#3498db1f' : 'transparent',
                        color: active ? '#3498db' : T.textSub,
                      }}>
                      {ch.label}{!ch.ok ? ` — ${ch.missing}` : ''}
                    </button>
                  );
                })}
              </div>
            </div>

            {supersedesId && (
              <div style={{ fontSize: 12, color: '#e67e22', marginBottom: 12, lineHeight: 1.6 }}>
                Replacing an expired link. Confirm the amount is still correct — the ticket may have changed since the original was sent.
              </div>
            )}
            {reqError && <div style={{ fontSize: 12.5, color: '#e74c3c', marginBottom: 12 }}>{reqError}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={{ ...S.btn(false), fontSize: 13, padding: '7px 16px', color: T.text, borderColor: T.border }}
                disabled={reqSending} onClick={resetRequest}>Cancel</button>
              <button style={{ ...S.btn(true), fontSize: 13, padding: '7px 16px', background: '#e67e22', color: '#fff', opacity: reqSending ? 0.6 : 1 }}
                disabled={reqSending} onClick={submitRequest}>
                {reqSending ? 'Sending…' : 'Create and send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#2ecc71', color: '#fff', padding: '11px 22px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, zIndex: 1100 }}>
          {toast}
        </div>
      )}
    </div>
  );
}

export default Payments;